import { useCallback, useRef, useState } from "react";
import { useSite } from "@/hooks/useSite";

// Cloudflare Turnstile (explicit render, "interaction-only" appearance: invisible unless Cloudflare needs a click) + a honeypot.
// Site key comes from /api/public/site turnstile_site_key; empty → no widget, forms still work (the server decides).
const SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let loader = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loader) loader = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SRC; s.async = true; s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => { loader = null; reject(new Error("turnstile_script_failed")); };
    document.head.appendChild(s);
  });
  return loader;
}

export const BOT_FAILED = "We couldn't confirm you're a person. Please try again in a moment.";
export const TOO_MANY = "Too many tries, please wait a few minutes.";
// Friendly text for the two bot-protection answers; null for anything else.
export const botMessage = (err) => (err?.code === "bot_check_failed" ? BOT_FAILED : err?.status === 429 ? TOO_MANY : null);

export function useBotCheck(action) {
  const siteKey = useSite().data?.turnstile_site_key || "";
  const widget = useRef(null);
  const slotEl = useRef(null);
  const token = useRef(null);
  const waiters = useRef([]);
  const [hp, setHp] = useState("");
  const [widgetError, setWidgetError] = useState(null);
  const flush = (t) => { waiters.current.splice(0).forEach((w) => w(t)); };

  // Callback ref: render when the slot mounts, remove when it unmounts (forms swap to a success view and back).
  const attach = useCallback((el) => {
    if (slotEl.current === el) return;
    slotEl.current = el;
    if (widget.current != null) { try { window.turnstile?.remove(widget.current); } catch { /* already gone */ } widget.current = null; token.current = null; }
    if (!el || !siteKey) return;
    loadTurnstile().then((ts) => {
      // StrictMode attaches refs twice; render once, into the slot that is still mounted.
      if (!el.isConnected || slotEl.current !== el || widget.current != null) return;
      widget.current = ts.render(el, {
        sitekey: siteKey, action, appearance: "interaction-only",
        callback: (t) => { token.current = t; setWidgetError(null); flush(t); },
        "expired-callback": () => { token.current = null; },
        "error-callback": (code) => { token.current = null; setWidgetError(String(code || "error")); console.warn(`[bot check] Turnstile error ${code}`); flush(null); return true; },
      });
    }).catch((e) => { setWidgetError(e.message); flush(null); });
  }, [siteKey, action]);

  // Resolves with the token (waits up to 10 s for a managed challenge), or null if the widget can't produce one.
  const getToken = () => {
    if (!siteKey) return Promise.resolve(null);
    if (token.current) return Promise.resolve(token.current);
    if (widgetError) return Promise.resolve(null);
    return new Promise((resolve) => { waiters.current.push(resolve); setTimeout(() => resolve(token.current), 10000); });
  };
  const reset = () => { token.current = null; setWidgetError(null); try { if (widget.current != null) window.turnstile?.reset(widget.current); } catch { /* removed */ } };
  const fields = (t) => ({ turnstile_token: t || null, website: hp });

  const slot = siteKey ? <div ref={attach} className="empty:hidden" data-testid={`${action}-turnstile`} data-widget-error={widgetError || undefined} /> : null;
  const honeypot = (
    <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
      <label htmlFor={`${action}-website`}>Website</label>
      <input id={`${action}-website`} type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} data-testid={`${action}-honeypot`} />
    </div>
  );
  return { enabled: !!siteKey, slot, honeypot, getToken, reset, fields, widgetError };
}
